import {useEffect} from 'react';
import {AppState, Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {
    addForegroundMessageListener,
    addNotificationOpenedListener,
    addTokenRefreshListener,
    consumePendingOpenNotification,
    getInitialOpenNotification,
    savePendingOpenNotification,
} from '../services/pushNotifications';
import {bootstrapPushNotificationRegistration, syncRefreshedNotificationToken,} from '../services/pushNotificationSync';
import {markNotificationLaunchIntent} from '../helpers/notificationLaunchIntent';
import {navigateFromNotificationMessage, resolveNotificationNavigationTarget} from '../helpers/notificationNavigation';
import {handleDriverForegroundPush, isDriverTarget, openDriverTarget} from '../helpers/driverPushRouting';
import {
    ensureDefaultPushChannel,
    installMailNotificationFastPath,
    observeMailPushPayload,
    presentPushAsLocalNotification,
} from '../helpers/mailNotifications';
import {cameToForeground} from '../helpers/orderSyncReliability';
import {refreshAppBadge} from '../helpers/appBadge';
import {isPushForActiveChatView} from '../helpers/teamChat/activeChatView';
import i18n from '../../i18n';

// Skip a resume-triggered re-sync if the last one (cold start or a previous
// resume) ran more recently than this — the backend just needs `lastSeenAt`
// to stay inside its device-recency window, not a sync on every tap-tap
// background/foreground toggle.
const FOREGROUND_RESYNC_MIN_INTERVAL_MS = 5 * 60 * 1000;

// NOTE: No first-login "enable notifications" prompt. Push is opt-in ONLY — the
// user turns it on manually from Settings → Notifications (which runs the same
// enablePushNotifications() flow: request OS permission → register token →
// backend flips pushEnabled on). Prompting on login was removed because tapping
// the prompt opened the OS permission dialog mid-modal-close, stealing focus and
// freezing the app (see HappyAlertHost close() watchdog). If permission was
// already granted on a prior install/session, bootstrapPushNotificationRegistration()
// below still silently re-syncs the token (it never re-prompts).

// A foregrounded app never renders the FCM `notification` block itself, so we
// re-present the push as a LOCAL notification: visible, sounds, tappable and
// deep-linking through the shared responder. Falls back to a transient in-app
// toast only when the local-notification module is unavailable (rare builds).
const presentForegroundPush = async (message) => {
    // Silent, data-only home-screen widget refreshes (`widget.refresh`, fired by
    // the backend widgetSync service on every timer start/pause/break/resume/stop)
    // must NEVER surface as a banner: they carry no `notification` block and would
    // otherwise render as a generic "New notification" every time the admin panel
    // touches the timer. App.jsx owns the foreground handling (it calls
    // refreshWidgets()); index.js owns the background one. Both stay invisible.
    if (message?.data?.type === 'widget.refresh') {
        return;
    }

    // Team Chat message of the conversation already open on screen: it is visible
    // in the list already, so no banner, toast or sound.
    if (isPushForActiveChatView(message?.data)) {
        return;
    }

    // Driver app: while the courier is in driver mode the push is shown as a big
    // in-app banner (or on the open route screen) instead of a tray notification.
    if (handleDriverForegroundPush(message)) {
        return;
    }

    const presented = await presentPushAsLocalNotification(message);
    if (presented) {
        return;
    }
    Toast.show({
        type: 'info',
        text1: message?.notification?.title || i18n.t('notif_foreground_default_title', {defaultValue: 'New notification'}),
        text2: message?.notification?.body || i18n.t('notif_foreground_default_body', {defaultValue: 'Open the app to review the update.'}),
        visibilityTime: 5000,
    });
};

export default function PushNotificationsBootstrap({ navigation }) {
    useEffect(() => {
        if (Platform.OS !== 'android' && Platform.OS !== 'ios') {
            return undefined;
        }

        let isMounted = true;
        let appStateRef = { current: AppState.currentState || 'active' };
        let lastTokenSyncAtRef = { current: 0 };

        const resyncTokenIfDue = async (reason) => {
            const now = Date.now();
            if (now - lastTokenSyncAtRef.current < FOREGROUND_RESYNC_MIN_INTERVAL_MS) {
                return;
            }
            lastTokenSyncAtRef.current = now;
            try {
                // Silently re-syncs the already-registered token (never prompts) so
                // `UserDevice.lastSeenAt` stays fresh — see bootstrap() below for the
                // cold-start call of the same function.
                await bootstrapPushNotificationRegistration();
            } catch (error) {
                console.warn(`[PushNotificationsBootstrap] ${reason} sync failed:`, error?.message || error);
            }
        };

        const tryNavigateFromMessage = async (message) => {
            if (!message || !isMounted) {
                return;
            }

            // Flag the launch as notification-driven so the boot surfaces get out
            // of the way (SplashScreenWrapper skips the branding animation, the
            // auto-tutorial stands down). This is the FCM-side marker; taps that
            // expo-notifications sees — every local banner, and remote taps on
            // iOS — are already marked far earlier by the module-scope responder
            // in helpers/mailNotifications, before React even mounts.
            markNotificationLaunchIntent({source: 'fcm-tap'});

            // Mail fast-path FIRST: cache a stub from the payload (so the reader
            // paints sender/subject/preview on its first frame) and start the
            // body fetch, both fire-and-forget, so the network request overlaps
            // the navigation instead of starting after the screen mounted.
            observeMailPushPayload(message.data);

            // Driver pushes: one tap to the right place, switching into driver mode
            // first when the app is currently in another mode (see driverPushRouting).
            const driverTarget = resolveNotificationNavigationTarget(message);
            if (isDriverTarget(driverTarget)) {
                await openDriverTarget(driverTarget, message, (name, params) => navigation.navigate(name, params));

                return;
            }

            const handled = await navigateFromNotificationMessage(navigation, message);
            if (!handled) {
                await savePendingOpenNotification(message);
            }
        };

        const bootstrap = async () => {
            // Create the 'default' Android channel the backend targets BEFORE any
            // push can arrive, otherwise Android 8+ drops remote pushes silently.
            await ensureDefaultPushChannel();

            // Silently re-register the token only if the user previously enabled
            // push (permission already granted). Never prompts — enabling is a
            // manual, opt-in action from Settings → Notifications. Runs on every
            // cold start (this component mounts on the post-login home screens),
            // which also refreshes `UserDevice.lastSeenAt` on the backend so the
            // device stays inside its push-delivery recency window.
            lastTokenSyncAtRef.current = Date.now();
            try {
                await bootstrapPushNotificationRegistration();
            } catch (error) {
                console.warn('[PushNotificationsBootstrap] Bootstrap sync failed:', error?.message || error);
            }

            try {
                const initialMessage = await getInitialOpenNotification();
                if (initialMessage) {
                    await tryNavigateFromMessage(initialMessage);
                    return;
                }

                const pendingMessage = await consumePendingOpenNotification();
                if (pendingMessage) {
                    await tryNavigateFromMessage(pendingMessage);
                }
            } catch (error) {
                console.warn('[PushNotificationsBootstrap] Initial notification handling failed:', error?.message || error);
            }
        };

        bootstrap();

        // Paint the app icon badge (unread notifications + unread mail) as soon as the
        // post-login shell mounts, so a cold start never shows a stale count.
        refreshAppBadge({force: true});

        // Registering FCM listeners touches the native messaging() module, which can
        // throw synchronously on devices without Google Play Services (AOSP POS / kiosk
        // hardware). Guard it so a GMS-less device never crashes the home screen — push
        // just stays inactive there, matching the guarded token registration above.
        let unsubscribeForeground;
        let unsubscribeOpened;
        let unsubscribeTokenRefresh;
        try {
            unsubscribeForeground = addForegroundMessageListener(async (message) => {
                await presentForegroundPush(message);
                // A push that arrived while we are foregrounded means one more unread
                // row on the server — re-read both counters instead of guessing +1.
                refreshAppBadge();
            });

            unsubscribeOpened = addNotificationOpenedListener(async (message) => {
                await tryNavigateFromMessage(message);
                refreshAppBadge();
            });

            unsubscribeTokenRefresh = addTokenRefreshListener(async (token) => {
                try {
                    await syncRefreshedNotificationToken(token);
                } catch (error) {
                    console.warn('[PushNotificationsBootstrap] Token refresh sync failed:', error?.message || error);
                }
            });
        } catch (error) {
            console.warn('[PushNotificationsBootstrap] FCM listener registration failed (no Play Services?):', error?.message || error);
        }

        // Route taps on LOCAL mail notifications (the FCM-push fallback) to the
        // right thread, same as remote push taps. Normally already installed at
        // module scope (helpers/mailNotifications) so a cold-start tap is caught
        // before this component ever mounts; this call is the idempotent safety
        // net and returns a no-op unsubscribe in that case.
        const unsubscribeMailResponder = installMailNotificationFastPath();

        // Re-sync on foreground-resume too, not just cold start: the OS can keep
        // the app (and its Firebase token registration) alive across days of
        // background/foreground cycles without ever re-launching JS, so without
        // this `UserDevice.lastSeenAt` goes stale and the backend's device-recency
        // window drops the device from push delivery. syncNotificationTokenWithBackend
        // already serializes overlapping calls (runSerialSync) and the backend
        // upserts by device, so this is idempotent even if it raced another sync.
        const appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
            const prevAppState = appStateRef.current;
            appStateRef.current = nextAppState;
            if (!cameToForeground(prevAppState, nextAppState)) {
                return;
            }
            resyncTokenIfDue('Foreground-resume');
            // Reads/deletes may have happened on the web admin (or in the shade) while
            // we were backgrounded — the badge must match the server on resume.
            refreshAppBadge({force: true});
        });

        return () => {
            isMounted = false;
            unsubscribeForeground?.();
            unsubscribeOpened?.();
            unsubscribeTokenRefresh?.();
            unsubscribeMailResponder?.();
            appStateSubscription?.remove?.();
        };
    }, [navigation]);

    return null;
}


